import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, effect, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ACTIVITY_TYPE_LABELS, ENGAGEMENT_LABELS } from "./engagement.labels";
import { EngagementActivityType, EngagementService } from "./engagement.service";
import { S25Util } from "../../../util/s25-util";

type ActivityRow = Pick<EngagementActivityType, "activityTypeKey" | "weight" | "isActive">;

@TypeManagerDecorator("s25-ng-se-engagement-config")
@Component({
    selector: "s25-ng-se-engagement-config",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule],
    template: `
        <div class="se-engagement-config">
            <h2 class="se-engagement-config__title">{{ labels.configTitle }}</h2>

            @if (flashMessage()) {
                <div class="se-engagement-config__flash" role="status">{{ flashMessage() }}</div>
            }
            @if (loadError()) {
                <div class="se-engagement-config__error" role="alert">{{ loadError() }}</div>
            }

            <section class="se-engagement-config__section">
                <h3>{{ labels.activityTypesTitle }}</h3>
                @if (loading()) {
                    <p class="se-engagement-config__muted">Loading…</p>
                } @else if (activityRows().length) {
                    <table class="se-engagement-config__table">
                        <thead>
                            <tr>
                                <th>Label</th>
                                <th>Type key</th>
                                <th>Weight</th>
                                <th>Active</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of activityRows(); track row.activityTypeKey) {
                                <tr>
                                    <td>{{ activityLabel(row.activityTypeKey) }}</td>
                                    <td>
                                        <code>{{ row.activityTypeKey }}</code>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            class="se-engagement-config__input c-input"
                                            [ngModel]="row.weight"
                                            (ngModelChange)="patchRow(row.activityTypeKey, { weight: +$event })"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            class="c-input"
                                            [ngModel]="row.isActive"
                                            (ngModelChange)="patchRow(row.activityTypeKey, { isActive: $event })"
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            class="aw-button aw-button--primary aw-button--small"
                                            [disabled]="savingKey() === row.activityTypeKey"
                                            (click)="saveActivityRow(row.activityTypeKey)"
                                        >
                                            Save
                                        </button>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                } @else if (!loading()) {
                    <p class="se-engagement-config__muted">No activity types.</p>
                }
            </section>
        </div>
    `,
    styles: [
        `
            .se-engagement-config__title {
                margin: 0 0 1rem;
            }
            .se-engagement-config__section {
                margin-bottom: 2rem;
            }
            .se-engagement-config__section h3 {
                margin: 0 0 0.75rem;
            }
            .se-engagement-config__table {
                width: 100%;
                border-collapse: collapse;
            }
            .se-engagement-config__table th,
            .se-engagement-config__table td {
                text-align: left;
                padding: 0.5rem 0.75rem;
                border-bottom: 1px solid #e0e0e0;
            }
            .se-engagement-config__input {
                width: 5rem;
                max-width: 100%;
            }
            .se-engagement-config__flash {
                padding: 0.5rem 0.75rem;
                margin-bottom: 1rem;
                background: #e8f5e9;
                border-radius: 4px;
            }
            .se-engagement-config__error {
                padding: 0.5rem 0.75rem;
                margin-bottom: 1rem;
                background: #ffebee;
                border-radius: 4px;
            }
            .se-engagement-config__muted {
                color: #666;
            }
        `,
    ],
})
export class SeEngagementConfigComponent {
    readonly labels = ENGAGEMENT_LABELS;

    readonly loading = signal(false);
    readonly loadError = signal<string | undefined>(undefined);
    readonly activityRows = signal<ActivityRow[]>([]);
    readonly savingKey = signal<string | undefined>(undefined);
    readonly flashMessage = signal<string | undefined>(undefined);

    readonly activityLabel = (key: string) => ACTIVITY_TYPE_LABELS[key] ?? key;

    constructor() {
        effect((onCleanup) => {
            let cancelled = false;
            onCleanup(() => {
                cancelled = true;
            });
            void (async () => {
                this.loading.set(true);
                this.loadError.set(undefined);
                try {
                    const types = await EngagementService.getActivityTypes();
                    if (cancelled) return;
                    this.activityRows.set(
                        types.map((t) => ({
                            activityTypeKey: t.activityTypeKey,
                            weight: t.weight,
                            isActive: t.isActive,
                        })),
                    );
                } catch (e) {
                    if (!cancelled) {
                        this.loadError.set(S25Util.errorText(e));
                        this.activityRows.set([]);
                    }
                } finally {
                    if (!cancelled) this.loading.set(false);
                }
            })();
        });

        effect((onCleanup) => {
            const msg = this.flashMessage();
            if (!msg) return;
            const t = window.setTimeout(() => this.flashMessage.set(undefined), 4000);
            onCleanup(() => window.clearTimeout(t));
        });
    }

    patchRow(key: string, patch: Partial<Pick<ActivityRow, "weight" | "isActive">>): void {
        this.activityRows.update((rows) => rows.map((r) => (r.activityTypeKey === key ? { ...r, ...patch } : r)));
    }

    async saveActivityRow(activityTypeKey: string): Promise<void> {
        const row = this.activityRows().find((r) => r.activityTypeKey === activityTypeKey);
        if (!row) return;
        this.savingKey.set(activityTypeKey);
        try {
            await EngagementService.updateActivityType(activityTypeKey, row.weight, row.isActive);
            this.flashMessage.set("Activity type saved.");
        } catch (e) {
            this.loadError.set(S25Util.errorText(e));
        } finally {
            this.savingKey.set(undefined);
        }
    }
}
